import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Save } from "lucide-react";
import { ADMIN_PLANS_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

const TRUSTEDGE_FEATURES = [
    { key: 'dashboard',              label: 'Dashboard',         group: null },
    { key: 'customers',              label: 'Customers',         group: null },
    { key: 'followup',               label: 'Follow Up',         group: null },
    { key: 'tasks',                  label: 'Tasks',             group: null },
    { key: 'report_timeline',        label: 'Timeline',          group: 'Reports' },
    { key: 'report_lead_sources',    label: 'Lead Meta Form',    group: 'Reports' },
    { key: 'report_lead_types',      label: 'Lead Source',       group: 'Reports' },
    { key: 'report_lead_potential',  label: 'Lead Potential',    group: 'Reports' },
    { key: 'report_sales_forecast',  label: 'Sales Forecast',    group: 'Reports' },
    { key: 'report_engagement',      label: 'Engagement',        group: 'Reports' },
    { key: 'report_conversion',      label: 'Conversion',        group: 'Reports' },
    { key: 'report_email_analytics', label: 'Email Analytics',   group: 'Reports' },
    { key: 'whatsapp_campaigns',     label: 'Campaigns',         group: 'WhatsApp' },
    { key: 'whatsapp_inbox',         label: 'Inbox',             group: 'WhatsApp' },
    { key: 'whatsapp_integration',   label: 'WA Integration',    group: 'WhatsApp' },
    { key: 'whatsapp_templates',     label: 'WA Templates',      group: 'WhatsApp' },
    { key: 'email_integration',      label: 'Email Integration', group: 'Email' },
    { key: 'email_templates',        label: 'Email Templates',   group: 'Email' },
];

const ENGAGE_FEATURES = [
    { key: 'engage_dashboard',           label: 'Dashboard',           group: null },
    { key: 'engage_enquiries',           label: 'Patient Enquiries',   group: null },
    { key: 'engage_referring_doctors',   label: 'Referring Doctors',   group: null },
    { key: 'engage_ambulance',           label: 'Ambulance Partners',  group: null },
    { key: 'engage_corporate_accounts',  label: 'Corporate Accounts',  group: null },
    { key: 'engage_health_camps',        label: 'Health Camps',        group: null },
    { key: 'engage_digital_marketing',   label: 'Digital Marketing',   group: null },
    { key: 'engage_followups',           label: 'Follow-ups',          group: null },
    { key: 'engage_bulk_import',         label: 'Bulk Import',         group: null },
    { key: 'engage_reports',             label: 'Reports',             group: null },
    { key: 'engage_ai_caller',           label: 'AI Caller',           group: null },
    { key: 'engage_partner_portal',      label: 'Partner Portal',      group: null },
    { key: 'whatsapp_campaigns',         label: 'Campaigns',           group: 'WhatsApp' },
    { key: 'whatsapp_inbox',             label: 'Inbox',               group: 'WhatsApp' },
    { key: 'whatsapp_integration',       label: 'WA Integration',      group: 'WhatsApp' },
    { key: 'email_integration',          label: 'Email Integration',   group: 'Email' },
];

const FEATURES_BY_PRODUCT = {
    trustedge: TRUSTEDGE_FEATURES,
    engage:    ENGAGE_FEATURES,
};

const featuresForProduct = (pt) => FEATURES_BY_PRODUCT[pt] || TRUSTEDGE_FEATURES;

const EMPTY_PLAN = (product_type = 'trustedge') => ({
    id: null, name: '', description: '', price_monthly: '', max_users: 10, billing_cycle: 'monthly',
    product_type,
    features: Object.fromEntries(featuresForProduct(product_type).map(f => [f.key, false])),
});

const ManagePlans = () => {
    const navigate       = useNavigate();
    const { fetch, create, update } = useApiMethods();
    const [plans,   setPlans]   = useState([]);
    const [editing, setEditing] = useState(null);   // plan form
    const [saving,  setSaving]  = useState(false);
    const [loading, setLoading] = useState(false);

    const loadPlans = async () => {
        setLoading(true);
        const res = await fetch(ADMIN_PLANS_URL, {}, setLoading, true);
        if (res?.status === "success") setPlans(res.data || []);
        setLoading(false);
    };

    useEffect(() => { loadPlans(); }, []);

    const handleEdit = (plan) => {
        const pt = plan.product_type || 'trustedge';
        setEditing({
            id: plan.id, name: plan.name, description: plan.description,
            price_monthly: plan.price_monthly, max_users: plan.max_users, billing_cycle: plan.billing_cycle,
            product_type: pt,
            features: { ...Object.fromEntries(featuresForProduct(pt).map(f => [f.key, false])), ...plan.features },
        });
    };

    const handleNew = () => setEditing(EMPTY_PLAN());

    const setEditingProduct = (pt) => {
        setEditing(ed => ({
            ...ed,
            product_type: pt,
            features: Object.fromEntries(featuresForProduct(pt).map(f => [f.key, false])),
        }));
    };

    const handleSave = async () => {
        if (!editing.name.trim()) { ToastMsg("error", "Plan name required"); return; }
        setSaving(true);
        const isEdit = !!editing.id;
        const fn = isEdit ? update : create;
        const res = await fn(ADMIN_PLANS_URL, editing, false, setSaving, true);
        if (res?.status === "success") {
            ToastMsg("success", isEdit ? "Plan updated" : "Plan created");
            setEditing(null);
            loadPlans();
        } else {
            ToastMsg("error", res?.message || "Failed to save plan");
        }
        setSaving(false);
    };

    const activeFeatures = editing ? featuresForProduct(editing.product_type) : TRUSTEDGE_FEATURES;
    const groups         = [...new Set(activeFeatures.map(f => f.group))];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
            <div className="max-w-5xl mx-auto space-y-6">
                <button onClick={() => navigate("/admin/dashboard")}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                    <ArrowLeft className="w-4 h-4" /> Back to Dashboard
                </button>

                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Plans</h1>
                    <button onClick={handleNew}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                        <Plus className="w-4 h-4" /> New Plan
                    </button>
                </div>

                {/* Plan Editor */}
                {editing && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-indigo-200 dark:border-indigo-700 p-6 space-y-5">
                        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{editing.id ? "Edit Plan" : "New Plan"}</h2>

                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2">Product</label>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { value: 'trustedge', label: 'Gro Wing CRM',   description: 'Standard sales CRM' },
                                    { value: 'engage',    label: 'Hospital Engage', description: 'Hospital CRM' },
                                ].map(p => {
                                    const selected = editing.product_type === p.value;
                                    return (
                                        <label key={p.value}
                                            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${selected ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"}`}>
                                            <input type="radio" name="product_type" value={p.value} checked={selected} onChange={() => setEditingProduct(p.value)} className="mt-1" disabled={!!editing.id} />
                                            <div className="flex-1">
                                                <span className="font-medium text-sm text-gray-900 dark:text-white">{p.label}</span>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{p.description}</p>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            {editing.id && <p className="text-xs text-gray-400 mt-2">Product type is locked after creation — create a new plan to switch products.</p>}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                                { label: 'Plan Name *', key: 'name', placeholder: 'e.g. Starter' },
                                { label: 'Description', key: 'description', placeholder: 'Short description' },
                                { label: 'Price / Month (₹)', key: 'price_monthly', placeholder: '999' },
                                { label: 'Max Users', key: 'max_users', placeholder: '10' },
                            ].map(f => (
                                <div key={f.key}>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">{f.label}</label>
                                    <input value={editing[f.key] ?? ''} onChange={e => setEditing({ ...editing, [f.key]: e.target.value })}
                                        placeholder={f.placeholder}
                                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30" />
                                </div>
                            ))}
                        </div>

                        {/* Feature toggles */}
                        <div>
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Features Included</p>
                            {groups.map(group => {
                                const gf = activeFeatures.filter(f => f.group === group);
                                return (
                                    <Fragment key={group || 'main'}>
                                        {group && <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-3 mb-1.5">{group}</p>}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                                            {gf.map(f => (
                                                <button key={f.key} type="button"
                                                    onClick={() => setEditing(ed => ({ ...ed, features: { ...ed.features, [f.key]: !ed.features[f.key] } }))}
                                                    className={`px-3 py-1.5 text-xs rounded-lg border transition-colors text-left ${editing.features[f.key] ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-medium" : "border-gray-200 dark:border-gray-700 text-gray-500"}`}>
                                                    {editing.features[f.key] ? "✓ " : ""}{f.label}
                                                </button>
                                            ))}
                                        </div>
                                    </Fragment>
                                );
                            })}
                        </div>

                        <div className="flex gap-3">
                            <button onClick={() => setEditing(null)}
                                className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                                Cancel
                            </button>
                            <button onClick={handleSave} disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg">
                                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Plan"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Plan Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {loading ? (
                        <p className="text-sm text-gray-400 col-span-3 text-center py-8">Loading…</p>
                    ) : plans.map(p => {
                        const pt = p.product_type || 'trustedge';
                        const cardFeatures = featuresForProduct(pt);
                        const isEngage = pt === 'engage';
                        return (
                        <div key={p.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-gray-900 dark:text-white">{p.name}</h3>
                                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${isEngage ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"}`}>
                                            {isEngage ? "Engage" : "Gro Wing CRM"}
                                        </span>
                                    </div>
                                    <p className="text-sm text-indigo-600 font-semibold">₹{p.price_monthly}/mo</p>
                                </div>
                                <button onClick={() => handleEdit(p)}
                                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors">
                                    <Edit className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mb-3">{p.description || `Up to ${p.max_users} users`}</p>
                            <div className="flex flex-wrap gap-1">
                                {Object.entries(p.features || {}).filter(([, v]) => v).map(([k]) => (
                                    <span key={k} className="px-1.5 py-0.5 text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded">
                                        {cardFeatures.find(f => f.key === k)?.label || k}
                                    </span>
                                ))}
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ManagePlans;
